import type { InvoiceItem } from "@/lib/api/types";
import { groupPlates, type PlateGroup } from "./groupPlates";

/**
 * PAPER (2026-10-03) — the bill's sent plates as the FIRES they left in, the
 * design's "On the table" ticket blocks: newest first, each numbered by the
 * order it went out in (one fire, one number — D21's round), each with the
 * least-advanced state of its live plates. Plates are split by fire BEFORE
 * they are grouped, so two colas sent in two rounds sit in their own rounds.
 */
export type FireState = "QUEUED" | "PREPARING" | "READY" | "SERVED";
const FIRE_PROGRESS: readonly FireState[] = ["QUEUED", "PREPARING", "READY", "SERVED"];

export function paperFires(
  items: InvoiceItem[],
  money: (value: number) => string,
): {
  key: string;
  round: number;
  firedAt: string | null;
  state: FireState | null;
  groups: PlateGroup[];
}[] {
  const byFire = new Map<string, InvoiceItem[]>();
  for (const item of items) {
    const key = item.firedAt ?? "";
    byFire.set(key, [...(byFire.get(key) ?? []), item]);
  }
  // ISO instants sort as text; a plate with no fire time goes last.
  const keys = [...byFire.keys()].sort((a, b) =>
    a === "" ? 1 : b === "" ? -1 : a.localeCompare(b),
  );
  return keys
    .map((key, index) => {
      const members = byFire.get(key) ?? [];
      const live = members
        .filter((item) => item.fulfillmentState !== "VOIDED")
        .map((item) => item.fulfillmentState);
      return {
        key: key || "unfired",
        round: index + 1,
        firedAt: key || null,
        state: FIRE_PROGRESS.find((state) => live.includes(state)) ?? null,
        groups: groupPlates(members, money),
      };
    })
    .reverse();
}


export type StatusSectionState = FireState | "VOIDED";

/**
 * The bill's sent plates grouped by WHERE THEY ARE, not by the round they left
 * in (founder, 2026-10-06: "I don't like the grouping by round — group by
 * status"). A waiter reads the bill for what still needs doing: what is waiting
 * for the kitchen, what is cooking, what is ready to carry out, what is on the
 * table. Rounds answered "when was it ordered", which nobody acts on.
 *
 * Lifecycle order (waiting → cooking → ready → served), cancelled plates last.
 * Plates are grouped within their state, so two colas sent in two rounds that
 * are both waiting read as one line of two.
 */
export function paperStatusSections(
  items: InvoiceItem[],
  money: (value: number) => string,
): { state: StatusSectionState; count: number; groups: PlateGroup[] }[] {
  const order: readonly StatusSectionState[] = [...FIRE_PROGRESS, "VOIDED"];
  return order
    .map((state) => {
      const members = items.filter((item) => item.fulfillmentState === state);
      return { state, count: members.length, groups: groupPlates(members, money) };
    })
    .filter((section) => section.count > 0);
}
